import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { IconButton } from './IconButton';

/**
 * A manual "pull the latest from the backend" affordance - wraps IconButton
 * with a spin animation and disabled state while `onSync` is in flight, and
 * swallows a rejected promise (mirrors this app's existing fire-and-forget
 * sync calls elsewhere - see StoreContext.tsx's patchChannel comment on why
 * a failed background sync shouldn't surface as a blocking error) so a
 * flaky connection just leaves the spinner stopping, not an uncaught
 * rejection.
 */
export function SyncButton({ onSync, ariaLabel = 'Sync' }: { onSync: () => Promise<void>; ariaLabel?: string }) {
  const [syncing, setSyncing] = useState(false);

  const handleClick = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await onSync();
    } catch {
      // best-effort - see this component's doc comment
    } finally {
      setSyncing(false);
    }
  };

  return (
    <IconButton
      icon={RefreshCw}
      onClick={handleClick}
      disabled={syncing}
      aria-label={ariaLabel}
      className={syncing ? 'animate-spin' : ''}
    />
  );
}
